import { Box } from "@mui/material";
import { createContext, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../App/Components/Navigation/Subcomponents/Breadcrumbs/Breadcrumbs";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { RandomTextSkeleton } from "../App/Components/Skeleton/RandomTextSkeleton";
import { useCandidateProfileQuery } from "../App/Queries/CandidateQueries";
import { useNotification } from "../App/State/Hooks/notificationHooks";
import { useAppSelector } from "../App/State/Hooks/reduxHooks";
import { useClientChangeCallback } from "../App/State/Hooks/useClientChangeCallback";
import { selectClientValue } from "../App/State/Slices/SelectedClientSlice";
import { ICandidateProfile } from "../App/Typings/CandidateProfile";
import MessageDialogCandidateEmail from "../MessageComponents/MessageDialog/MessageDialogCandidateEmail";
import MessageDialogCandidateText from "../MessageComponents/MessageDialog/MessageDialogCandidateText";
import { CandidateTabs } from "./Components/CandidateTabs/CandidateTabs";
import { CandidateTabContextProvider } from "./Components/CandidateTabs/State/CandidateTabsState";
import { ProfileInfo } from "./Components/ProfileInfo/ProfileInfo";

interface ICandidateProfileContext {
  candidate: ICandidateProfile;
}

export const CandidateContext = createContext<ICandidateProfileContext>({} as ICandidateProfileContext);

const getCandidateName = (candidate: ICandidateProfile | undefined): string =>
  candidate && candidate.firstName && candidate.lastName
    ? `${candidate.firstName} ${candidate.lastName}`
    : "";

/**
 * @description generates a candidate profile
 * @returns The candidate profile screen
 */
function CandidateProfile() {
  useClientChangeCallback("/candidates");
  const { id } = useParams();
  const navigate = useNavigate();
  const { clientId } = useAppSelector(selectClientValue);
  const { displayError } = useNotification();
  const { data: candidate, isLoading, error } = useCandidateProfileQuery(id ?? "");

  useEffect(() => {
    if (error || (!isLoading && !candidate)) {
      displayError("Error fetching candidate details");
      navigate("/candidates");
    }
    if (candidate && candidate.clientId && clientId && candidate.clientId !== clientId) {
      navigate("/candidates");
    }
  }, [navigate, candidate, clientId, error, displayError, isLoading]);

  return (
    <PageContainer>
      <Breadcrumb pageTitle={getCandidateName(candidate)} />
      <Box
        sx={{
          padding: "2rem 0 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "2rem",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {isLoading || !candidate ? (
            <RandomTextSkeleton />
          ) : (
            <CandidateContext.Provider value={{ candidate }}>
              <CandidateTabContextProvider>
                <Box sx={{ flex: 1 }}>
                  <ProfileInfo />
                </Box>
                <Box sx={{ flex: 2 }}>
                  <CandidateTabs />
                </Box>
              </CandidateTabContextProvider>
            </CandidateContext.Provider>
          )}
        </Box>
      </Box>
      {candidate && (
        <>
          <MessageDialogCandidateEmail candidate={candidate} />
          <MessageDialogCandidateText candidate={candidate} />
        </>
      )}
    </PageContainer>
  );
}

export default CandidateProfile;
